import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { MemoirSummary } from '../../../../types'
import { apiClient } from '../lib/api-client'
import { ApiError } from '../lib/errors'
import { SUPPORT_EMAIL } from '../lib/legal'
import ConfirmModal from './ui/confirm-modal'

/** "20 November 2026", in UTC, matching the date in the deletion email. */
export function formatMemoirDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  })
}

/**
 * How often, and for how long, the notice re-reads the Memoir after a refund
 * or a payment while it waits for Stripe's webhook. Locally the webhook
 * trailed the refund by ~8s; a minute covers a slow delivery without polling
 * forever.
 */
const WEBHOOK_POLL_MS = 2_000
const WEBHOOK_POLL_LIMIT = 30

/** Set on the pay-again success URL, so the page knows the buyer just paid. */
const PAID_AGAIN_PARAM = 'paid'

/**
 * The money side of one Memoir, under its name in the workspace (SOL-206):
 *
 * - read-only and waiting to be deleted: when, and a way to pay again;
 * - read-only with a dispute still open: uploads paused, nothing scheduled;
 * - active and inside its 30 days: a quiet way to refund it.
 *
 * Both money moves land later, through Stripe's webhook: the Memoir turns
 * read-only after a refund, and active again after a payment. Until then this
 * asks the workspace to reload it. Back from paying again, the button is gone
 * meanwhile: one still showing was how a buyer paid twice.
 */
export default function MemoirPaymentNotice({
  memoir,
  onChanged,
}: {
  memoir: MemoirSummary
  onChanged?: () => void
}): JSX.Element | null {
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const paidAgain = searchParams.get(PAID_AGAIN_PARAM) === 'again'
  // The status the webhook is expected to bring, while we wait for it.
  const [waitingFor, setWaitingFor] = useState<'grace' | 'active' | null>(paidAgain ? 'active' : null)

  // The workspace hands a fresh `onChanged` on every render, and every reload
  // re-renders; held in a ref so a reload does not restart the polling.
  const onChangedRef = useRef(onChanged)
  onChangedRef.current = onChanged

  useEffect(() => {
    if (!waitingFor || memoir.status === waitingFor) return
    let polls = 0
    const timer = setInterval(() => {
      polls += 1
      onChangedRef.current?.()
      if (polls >= WEBHOOK_POLL_LIMIT) clearInterval(timer)
    }, WEBHOOK_POLL_MS)
    return () => clearInterval(timer)
  }, [waitingFor, memoir.status])

  // Reopened: the marker has done its job, and a reload must not resurrect it.
  useEffect(() => {
    if (paidAgain && memoir.status === 'active') {
      setSearchParams((p) => { p.delete(PAID_AGAIN_PARAM); return p }, { replace: true })
    }
  }, [paidAgain, memoir.status, setSearchParams])

  async function refund(): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      await apiClient.refundMemoir(memoir.uuid)
      setConfirming(false)
      setMessage(
        memoir.refundKind === 'renewal'
          ? 'Refund requested. The money goes back to your payment method, usually within 5 to 10 days. '
            + 'This year is cancelled in a moment.'
          : 'Refund requested. The money goes back to your payment method, usually within 5 to 10 days. '
            + 'This Memoir becomes read-only in a moment.',
      )
      setWaitingFor('grace')
    } catch (e) {
      setConfirming(false)
      setError(e instanceof ApiError && e.status === 409
        ? e.message
        : `Could not refund this Memoir. Please try again, or write to ${SUPPORT_EMAIL}.`)
    } finally {
      setBusy(false)
    }
  }

  async function payAgain(): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      const here = `${window.location.origin}/memoir/${encodeURIComponent(memoir.uuid)}`
      const { url } = await apiClient.createMemoirRepurchase(memoir.uuid, `${here}?${PAID_AGAIN_PARAM}=again`, here)
      window.location.href = url
    } catch (e) {
      setError(e instanceof ApiError && e.status === 409
        ? e.message
        : 'Could not start checkout. Please try again.')
      setBusy(false)
    }
  }

  if (memoir.status === 'grace' && !memoir.lapsed && paidAgain) {
    return (
      <div className="memoir-scope-header__notice" role="status">
        <p>
          Payment received, thank you. This Memoir reopens as soon as the payment is confirmed,
          usually within a minute; for a bank transfer or direct debit it can take a few days.
          Nothing in it will be deleted meanwhile.
        </p>
      </div>
    )
  }

  if (memoir.status === 'grace' && !memoir.lapsed) {
    return (
      <div className="memoir-scope-header__notice" role="status">
        {memoir.deletionDueAt ? (
          <>
            <p>
              The payment for this Memoir was refunded or reversed, so it is read-only, and it
              will be <strong>deleted with everything in it on {formatMemoirDate(memoir.deletionDueAt)}</strong>.
              Until then its photos can still be viewed, shared and downloaded.
            </p>
            <button
              type="button"
              className="memoir-payment__action"
              onClick={() => void payAgain()}
              disabled={busy}
            >
              Pay again to keep it (€30 + VAT)
            </button>
          </>
        ) : (
          <p>
            The payment for this Memoir is disputed, so new uploads are paused. Its photos are
            safe and can still be viewed, shared and downloaded.
          </p>
        )}
        {error && <p className="memoir-payment__error" role="alert">{error}</p>}
      </div>
    )
  }

  if (!memoir.refundableUntil && !message && !error) return null

  return (
    <div className="memoir-payment">
      {message ? (
        <p className="memoir-payment__message" role="status">{message}</p>
      ) : memoir.refundableUntil && (
        <button
          type="button"
          className="memoir-payment__refund"
          onClick={() => setConfirming(true)}
          disabled={busy}
        >
          {memoir.refundKind === 'renewal' ? "Refund this year's payment" : 'Refund this Memoir'}
          {' '}(until {formatMemoirDate(memoir.refundableUntil)})
        </button>
      )}
      {error && <p className="memoir-payment__error" role="alert">{error}</p>}
      <ConfirmModal
        isOpen={confirming}
        title={memoir.refundKind === 'renewal' ? "Refund this year?" : "Refund this Memoir?"}
        message={
          memoir.refundKind === 'renewal'
            ? 'Refund this year\'s payment? You get the full amount back on the card you paid with. '
              + 'The year it paid for is cancelled. If your Memoir\'s earlier paid year has already '
              + 'ended, it becomes read-only again and keeps its earlier deletion date. '
              + 'A year can be refunded once.'
            : 'You get the full amount back on the card you paid with. This Memoir becomes read-only '
              + 'straight away, and 30 days later it is deleted with everything in it, unless you pay '
              + 'for it again. Download anything you want to keep first. A Memoir can be refunded once.'
        }
        confirmText="Refund"
        variant="warning"
        isLoading={busy}
        onConfirm={() => void refund()}
        onCancel={() => setConfirming(false)}
      />
    </div>
  )
}
